import { useEffect, useState } from 'react'

import { fetchMe } from '../api/me'
import type { AuthSession, OrganizationType, UserRole } from '../api/types'
import { isDevMode } from '../config/devMode'

export type UserContext = {
  role: UserRole
  organizationName: string | null
  loading: boolean
  error: string | null
}

const normalizeMockRole = (mockRole: string | undefined): UserRole | null => {
  if (mockRole === 'supplier' || mockRole === 'buyer') {
    return mockRole
  }
  if (mockRole === 'restaurant') {
    return 'buyer'
  }
  return null
}

const roleFromOrgType = (orgType: OrganizationType | undefined): UserRole | null => {
  if (orgType === 'supplier') {
    return 'supplier'
  }
  if (orgType === 'buyer') {
    return 'buyer'
  }
  return null
}

export const useUserContext = (session: AuthSession, devRoleOverride?: UserRole): UserContext => {
  const [state, setState] = useState<UserContext>({
    role: devRoleOverride ?? 'buyer',
    organizationName: null,
    loading: !devRoleOverride,
    error: null,
  })

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (isDevMode && devRoleOverride) {
        if (!cancelled) {
          setState({
            role: devRoleOverride,
            organizationName: devRoleOverride === 'supplier' ? 'DEV · Поставщик' : 'DEV · Покупатель',
            loading: false,
            error: null,
          })
        }
        return
      }

      const envRole = normalizeMockRole(import.meta.env.VITE_DEV_MOCK_ROLE)
      if (envRole) {
        if (!cancelled) {
          setState({
            role: envRole,
            organizationName: null,
            loading: false,
            error: null,
          })
        }
        return
      }

      try {
        const me = await fetchMe()
        const org = me.organizations.find((item) => item.id === session.organizationId)
        const role = roleFromOrgType(org?.org_type) ?? 'buyer'

        if (!cancelled) {
          setState({
            role,
            organizationName: org?.trade_name ?? org?.legal_name ?? null,
            loading: false,
            error: null,
          })
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            role: 'buyer',
            organizationName: null,
            loading: false,
            error: error instanceof Error ? error.message : 'Не удалось загрузить профиль',
          })
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [session.accessToken, session.organizationId, devRoleOverride])

  return state
}
